import React, { FC, useRef, useState, useEffect } from 'react';
import { Box, Typography, Button, Paper, Grid, Card, CardActionArea } from '@mui/material';
import TrendingUpIcon from '@mui/icons-material/TrendingUp';
import TravelExploreIcon from '@mui/icons-material/TravelExplore';
import DashboardIcon from '@mui/icons-material/Dashboard';
import InsightsIcon from '@mui/icons-material/Insights';
import CalculateIcon from '@mui/icons-material/Calculate';
import EditNoteIcon from '@mui/icons-material/EditNote';
import { UserProfile, UserIntent, UserGoal } from '../types';
import TextField from '@mui/material/TextField';
import Alert from '@mui/material/Alert';
import { COLOR_PRIMARY_1 } from 'src/colors';

interface GoalSelectionProps {
  profile: Partial<UserProfile>;
  intent: UserIntent;
  value?: UserGoal;
  onChange: (data: { goal: UserGoal; customGoal?: string }) => void;
  onNext: () => void;
  onBack: () => void;
  onIntentUpdate?: (intent: UserIntent) => void;
}

export const getAvailableGoals = (userProfile: UserProfile) => {
  const baseGoals = [
    {
      value: 'explore_markets',
      label: 'Explore new markets with high short-term rental potential',
      icon: <TravelExploreIcon fontSize="large" sx={{ color: COLOR_PRIMARY_1 }} />,
    },
    {
      value: 'compare_markets',
      label: 'Compare data across a few markets',
      icon: <DashboardIcon fontSize="large" sx={{ color: COLOR_PRIMARY_1 }} />,
    },
    {
      value: 'analyze_market',
      label: "Analyze a specific market you're interested in",
      icon: <InsightsIcon fontSize="large" sx={{ color: COLOR_PRIMARY_1 }} />,
    },
    {
      value: 'analyze_property',
      label: 'Analyze the profit potential of a specific property',
      icon: <CalculateIcon fontSize="large" sx={{ color: COLOR_PRIMARY_1 }} />,
    },
  ];

  // Add benchmark option for existing hosts
  if (userProfile.userType !== 'new_property_investor') {
    baseGoals.push({
      value: 'benchmark_listings',
      label: 'Benchmark or optimize existing Airbnb listings',
      icon: <TrendingUpIcon fontSize="large" sx={{ color: COLOR_PRIMARY_1 }} />,
    });
  }

  baseGoals.push({
    value: 'unclassified',
    label: 'Other (Please describe)',
    icon: <EditNoteIcon fontSize="large" sx={{ color: COLOR_PRIMARY_1 }} />,
  });

  return baseGoals;
};

const GoalSelection: FC<GoalSelectionProps> = ({
  profile,
  intent,
  value,
  onChange,
  onNext,
  onBack,
  onIntentUpdate,
}: GoalSelectionProps) => {
  const radioRefs = useRef<(HTMLInputElement | null)[]>([]);
  const [error, setError] = useState<string | null>(null);
  const [otherText, setOtherText] = useState('');
  const goalOptions = getAvailableGoals(profile as UserProfile);
  const otherInputRef = useRef<HTMLDivElement | null>(null);

  useEffect(() => {
    if (value === 'unclassified' && otherInputRef.current) {
      otherInputRef.current.scrollIntoView({ behavior: 'smooth', block: 'center' });
    }
  }, [value]);

  const handleSelect = (selected: string) => {
    setError(null);
    if (selected !== 'unclassified') {
      setOtherText('');
      onChange({ goal: selected as UserGoal });
      if (onIntentUpdate) {
        onIntentUpdate({
          ...intent,
          intent: selected as UserIntent['intent'],
        });
      }
    } else {
      onChange({ goal: 'unclassified' as UserGoal });
      if (onIntentUpdate) {
        onIntentUpdate({
          ...intent,
          intent: 'unclassified' as UserIntent['intent'],
        });
      }
    }
  };

  const handleNext = () => {
    if (!value) {
      setError('Please select an option to continue.');
      return;
    }
    if (value === 'unclassified' && !otherText.trim()) {
      setError('Please describe.');
      return;
    }
    setError(null);
    if (value === 'unclassified' && otherText.trim()) {
      onChange({ goal: 'unclassified' as UserGoal, customGoal: otherText });
      if (onIntentUpdate) {
        onIntentUpdate({
          ...intent,
          intent: 'unclassified' as UserIntent['intent'],
        });
      }
    }
    onNext();
  };

  // Keyboard navigation handler
  const handleKeyDown = (
    e: React.KeyboardEvent<HTMLDivElement>,
    idx: number,
    optionsArr: typeof filteredOptions
  ) => {
    if (['ArrowRight', 'ArrowDown'].includes(e.key)) {
      e.preventDefault();
      const nextIdx = (idx + 1) % optionsArr.length;
      if (radioRefs.current[nextIdx]) {
        radioRefs.current[nextIdx]!.focus();
      }
    } else if (['ArrowLeft', 'ArrowUp'].includes(e.key)) {
      e.preventDefault();
      const prevIdx = (idx - 1 + optionsArr.length) % optionsArr.length;
      if (radioRefs.current[prevIdx]) {
        radioRefs.current[prevIdx]!.focus();
      }
    } else if (e.key === ' ' || e.key === 'Enter') {
      e.preventDefault();
      handleSelect(optionsArr[idx].value);
    }
  };

  // Filter options based on userType
  const filteredOptions = goalOptions.filter(option => {
    if (option.value === 'benchmark_listings') {
      return profile.userType === 'property_investor' || profile.userType === 'property_manager';
    }
    return true;
  });

  return (
    <Paper
      elevation={3}
      sx={{
        p: { xs: 2, sm: 4 },
        maxWidth: 600,
        mx: 'auto',
        mt: 4,
        display: 'flex',
        flexDirection: 'column',
        maxHeight: '85vh',
        '@media (max-width:480px)': {
          mt: 1,
          minHeight: '100vh',
          maxWidth: '100vw',
          borderRadius: 0,
        },
      }}
    >
      <Box
        sx={{
          flex: 1,
          overflowY: 'auto',
          '@media (max-width:480px)': {
            maxHeight: 'calc(100vh - 120px)',
          },
        }}
      >
        <Typography variant="h5" gutterBottom sx={{ mt: { xs: 4, sm: 0 } }}>
          What's your primary goal?
        </Typography>
        {error && (
          <Alert severity="warning" sx={{ mb: 2 }}>
            {error}
          </Alert>
        )}
        <Box role="radiogroup" aria-label="What's your primary goal?" sx={{ mt: 2 }}>
          <Grid container spacing={2}>
            {filteredOptions.map((option, idx) => (
              <Grid item xs={12} sm={6} key={option.value}>
                <label style={{ width: '100%', display: 'block', cursor: 'pointer' }}>
                  <input
                    type="radio"
                    name="goalSelectionOption"
                    value={option.value}
                    checked={value === option.value}
                    onChange={() => handleSelect(option.value)}
                    ref={el => (radioRefs.current[idx] = el)}
                    tabIndex={value === option.value ? 0 : -1}
                    aria-checked={value === option.value}
                    role="radio"
                    style={{ position: 'absolute', opacity: 0, pointerEvents: 'none' }}
                    onKeyDown={e => handleKeyDown(e, idx, filteredOptions)}
                  />
                  <Card
                    variant={value === option.value ? 'elevation' : 'outlined'}
                    tabIndex={-1}
                    sx={{
                      border:
                        value === option.value
                          ? `2px solid ${COLOR_PRIMARY_1}`
                          : '1px solid #e0e0e0',
                      boxShadow: value === option.value ? 6 : 0,
                      background: value === option.value ? '#f0f7ff' : '#fff',
                      transition: '0.2s',
                      minHeight: 120,
                      display: 'flex',
                      alignItems: 'center',
                      justifyContent: 'center',
                      py: 3,
                      px: 2,
                      position: 'relative',
                      '&:focus-within': {
                        boxShadow: 8,
                      },
                      '@media (max-width:480px)': {
                        minHeight: 0,
                        py: 3,
                      },
                    }}
                    onClick={() => handleSelect(option.value)}
                  >
                    <CardActionArea
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        alignItems: 'center',
                        py: 0,
                        px: 0,
                        width: '100%',
                        height: '100%',
                      }}
                    >
                      <Box
                        sx={{
                          display: 'flex',
                          flexDirection: 'column',
                          alignItems: 'center',
                          width: '100%',
                        }}
                      >
                        {option.icon}
                        <Typography
                          variant="subtitle1"
                          sx={{
                            mt: 1,
                            fontWeight: 500,
                            maxWidth: '30ch',
                            wordBreak: 'break-word',
                            textAlign: 'center',
                            whiteSpace: 'pre-line',
                          }}
                        >
                          {option.label}
                        </Typography>
                      </Box>
                    </CardActionArea>
                  </Card>
                </label>
                {/* Show text field if 'Other' is selected */}
                {option.value === 'unclassified' && value === 'unclassified' && (
                  <Box sx={{ mt: 2 }} ref={otherInputRef}>
                    <TextField
                      fullWidth
                      placeholder="Please describe"
                      value={otherText}
                      onChange={e => {
                        const newValue = e.target.value || 'unclassified';
                        setOtherText(newValue);
                      }}
                      inputProps={{ maxLength: 80 }}
                    />
                  </Box>
                )}
              </Grid>
            ))}
          </Grid>
        </Box>
        <Box sx={{ mt: 4, display: 'flex', justifyContent: 'center' }}>
          {/* Progress dots: step 3 of 3 */}
          <Box sx={{ display: 'flex', gap: 1 }}>
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#e0e0e0' }} />
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: '#e0e0e0' }} />
            <Box sx={{ width: 10, height: 10, borderRadius: '50%', bgcolor: COLOR_PRIMARY_1 }} />
          </Box>
        </Box>
      </Box>
      <Box sx={{ mt: 3, display: 'flex', justifyContent: 'space-between', pb: { xs: 2, sm: 0 } }}>
        <Button variant="outlined" onClick={onBack}>
          Back
        </Button>
        <Button
          variant="contained"
          color="primary"
          onClick={handleNext}
          disabled={!value || (value === 'unclassified' && !otherText.trim())}
        >
          Next
        </Button>
      </Box>
    </Paper>
  );
};

export default GoalSelection;
